import React, { useMemo } from "react";
import { Table, Tag, Space } from "antd";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import useFormattedDate from "hooks/useFormatDate";

export const urgencyColors = {
  LOW: { bg: "#f0f5ff", color: "#1d39c4", border: "#adc6ff" },
  MEDIUM: { bg: "#fff7e6", color: "#ad4e00", border: "#ffd591" },
  HIGH: { bg: "#fff1f0", color: "#cf1322", border: "#ffa39e" },
};

export const statusColors = {
  READ: { bg: "#e8f7e3", color: "#2e7d32", border: "#d5ebd0" },
  NEW: { bg: "#e6f7ff", color: "#096dd9", border: "#91d5ff" },
  UNREAD: { bg: "#f5f5f5", color: "#595959", border: "#d9d9d9" },
  DELIVERED: { bg: "#fff7e6", color: "#ad4e00", border: "#ffd591" },
};

export const getTagStyle = (type, value) => {
  const colors =
    type === "urgency" ? urgencyColors[value] : statusColors[value];
  return {
    backgroundColor: colors?.bg || "#f5f5f5",
    color: colors?.color || "#000",
    padding: "1px 8px",
    borderRadius: "6px",
    fontSize: "0.65rem",
    border: `1px solid ${colors?.border || "#d9d9d9"}`,
    cursor: "pointer",
    fontWeight: 600,
    fontFamily: "Inter",
  };
};

export default function ActionablesTable({
  actionables,
  filters,
  pagination,
  setPagination,
  onView,
  onEdit,
}) {
  const { formatDateTime } = useFormattedDate();

  // apply filters
  const filteredActionables = useMemo(() => {
    return actionables.filter((a) => {
      if (filters.status !== "ALL" && a.status !== filters.status) return false;
      if (filters.urgency !== "ALL" && a.urgency !== filters.urgency)
        return false;
      if (
        filters.displayType !== "ALL" &&
        a.displayType !== filters.displayType
      )
        return false;
      if (
        filters.search &&
        !a.message.toLowerCase().includes(filters.search.toLowerCase())
      )
        return false;
      return true;
    });
  }, [actionables, filters]);

  const columns = [
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      ellipsis: { showTitle: true },
      render: (text) => <span style={{ fontSize: 13 }}>{text}</span>,
    },
    {
      title: "Type",
      dataIndex: "actionItemType",
      key: "type",
      width: 180,
      render: (text) => (
        <Tag
          color="default"
          style={{ fontSize: 12, fontFamily: "Inter", borderRadius: "2px" }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Display",
      dataIndex: "displayType",
      key: "display",
      render: (text) => <span style={{ fontSize: 12 }}>{text}</span>,
    },
    {
      title: "Urgency",
      dataIndex: "urgency",
      key: "urgency",
      render: (text) => (
        <Tag key={text} style={getTagStyle("urgency", text)}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Tag key={text} style={getTagStyle("status", text)}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (text) => (
        <span style={{ fontSize: 12 }}>{formatDateTime(text)}</span>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <EyeOutlined
            style={{ cursor: "pointer" }}
            onClick={() => onView(record)}
          />
          <EditOutlined
            style={{ cursor: "pointer" }}
            onClick={() => onEdit(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={filteredActionables}
      rowKey="id"
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: filteredActionables.length,
        showSizeChanger: true,
      }}
      onChange={(pag) =>
        setPagination({ current: pag.current, pageSize: pag.pageSize })
      }
      size="small"
      className="custom-table"
      scroll={{ x: 900 }}
    />
  );
}
